import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SelectMenuProps {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
  className?: string;
}

/**
 * Application-styled dropdown, in place of the native `select`.
 *
 * A native select renders the operating system's own popup: it covers the
 * field, ignores the application's theme and, on macOS, opens centred on the
 * current value. This one always opens downwards, keeps the width of its
 * trigger and is painted from the same tokens as the rest of the interface,
 * while Radix keeps the keyboard and screen-reader behaviour of a listbox.
 */
export const SelectMenu = ({
  value,
  onValueChange,
  children,
  id,
  invalid = false,
  disabled = false,
  placeholder,
  className,
  ...aria
}: SelectMenuProps) => (
  <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
    <RadixSelect.Trigger
      id={id}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={aria['aria-describedby']}
      aria-label={aria['aria-label']}
      className={cn(
        'flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-surface px-2.5',
        'text-left text-[14px] text-ink transition-colors',
        'disabled:cursor-not-allowed disabled:bg-sunken',
        'data-[placeholder]:text-ink-subtle',
        invalid ? 'border-fail' : 'border-border hover:border-border-strong',
        className,
      )}
    >
      <RadixSelect.Value placeholder={placeholder} />
      <RadixSelect.Icon className="text-ink-subtle">
        <ChevronDown className="size-4" />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>

    <RadixSelect.Portal>
      <RadixSelect.Content
        // popper + side="bottom" is what keeps the list under the field
        // instead of over it; the width follows the trigger.
        position="popper"
        side="bottom"
        align="start"
        sideOffset={4}
        collisionPadding={8}
        className={cn(
          'z-50 max-h-72 w-[var(--radix-select-trigger-width)] overflow-hidden',
          'rounded-lg border border-border bg-surface shadow-lg',
        )}
      >
        <RadixSelect.Viewport className="p-1">{children}</RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  </RadixSelect.Root>
);

export interface SelectMenuItemProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
}

export const SelectMenuItem = forwardRef<HTMLDivElement, SelectMenuItemProps>(
  ({ value, children, disabled = false }, ref) => (
    <RadixSelect.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-md py-1.5 pl-7 pr-2',
        'text-[13px] text-ink outline-none',
        'data-[highlighted]:bg-sunken data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
      )}
    >
      <RadixSelect.ItemIndicator className="absolute left-2 text-accent-fg">
        <Check className="size-3.5" />
      </RadixSelect.ItemIndicator>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  ),
);
SelectMenuItem.displayName = 'SelectMenuItem';

export const SelectMenuLabel = ({ children }: { children: ReactNode }) => (
  <RadixSelect.Label className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
    {children}
  </RadixSelect.Label>
);
